import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useExpense } from "@/contexts/ExpenseContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { Calendar, Plus, Edit, Trash2, Clock, RefreshCw, CreditCard, Repeat, Filter, Eye, EyeOff, TrendingUp, DollarSign, ListChecks, Search, X, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { toast as sonnerToast } from "sonner";
import { useTranslation } from "react-i18next";
import { RepeatScheduleSelector, RepeatSchedule, createRepeatScheduleFromPayment, repeatScheduleToApiFields } from "@/components/RepeatScheduleSelector";
import { recurringPaymentApi } from "@/lib/api";

const colorOptions = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', 
  '#DDA0DD', '#98D8C8', '#FF8A80', '#82B1FF', '#B39DDB'
];

const dayOptions = [
  { value: 1, label: '1st of the month' },
  { value: 5, label: '5th of the month' },
  { value: 10, label: '10th of the month' },
  { value: 15, label: '15th of the month' },
  { value: 20, label: '20th of the month' },
  { value: 25, label: '25th of the month' },
  { value: 30, label: '30th of the month' }
];

export default function PeriodicPayments() {
  const { periodicPayments, categories, addPeriodicPayment, updatePeriodicPayment, deletePeriodicPayment, loadAllPeriodicPayments, loadActivePeriodicPayments, loading } = useExpense();
  const { formatAmount, currency } = useCurrency();
  const { language } = useLanguage();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [isOpen, setIsOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<any>(null);
  const [isFirstMount, setIsFirstMount] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const defaultRepeatSchedule: RepeatSchedule = {
    frequency: 'monthly',
    interval: 1,
    onDate: new Date().getDate(),
    endType: 'never',
  };

  const isMobile = useIsMobile();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMonth, setSyncMonth] = useState(new Date().getMonth() + 1);
  const [syncYear, setSyncYear] = useState(new Date().getFullYear());
  const [syncPopoverOpen, setSyncPopoverOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'active' | 'inactive' | 'all'>('active');

  const [formData, setFormData] = useState({
    paymentType: 'periodic', // 'periodic' or 'installment'
    amount: '', // For periodic: monthly amount, For installment: calculated amount
    totalAmount: '', // For installment: total amount to be paid
    category: '',
    description: '',
    color: colorOptions[0],
    dayOfMonth: 1,
    firstPaymentDate: new Date().toISOString().split('T')[0], // Default to today
    totalPayments: '', // For periodic: calculated, For installment: user input
    expirationDate: '', // For periodic: user input, For installment: calculated
    isActive: true,
    repeatSchedule: defaultRepeatSchedule,
  });

  const [initialLoading, setInitialLoading] = useState(true);

  // Calculate expiration date for installment payments (based on first payment date, payment day, and total payments)
  useEffect(() => {
    if (formData.paymentType === 'installment' && formData.firstPaymentDate && formData.totalPayments && parseInt(formData.totalPayments) > 0) {
      const calculateExpirationDate = () => {
        const firstDate = new Date(formData.firstPaymentDate);
        const totalPayments = parseInt(formData.totalPayments);
        const paymentDay = formData.dayOfMonth;
        
        // Start from the first payment date
        let currentDate = new Date(firstDate);
        
        // Adjust to the correct payment day if needed
        if (currentDate.getDate() !== paymentDay) {
          currentDate.setDate(paymentDay);
          // If the payment day has already passed this month, move to next month
          if (currentDate < firstDate) {
            currentDate.setMonth(currentDate.getMonth() + 1);
          }
        }
        
        // Add (totalPayments - 1) months to get the last payment date
        currentDate.setMonth(currentDate.getMonth() + (totalPayments - 1));
        
        // Handle edge cases for months with different numbers of days
        const targetDay = paymentDay;
        const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
        
        if (targetDay > lastDayOfMonth) {
          currentDate.setDate(lastDayOfMonth);
        } else {
          currentDate.setDate(targetDay);
        }
        
        return currentDate.toISOString().split('T')[0];
      };
      
      const calculatedDate = calculateExpirationDate();
      setFormData(prev => ({ ...prev, expirationDate: calculatedDate }));
    }
  }, [formData.paymentType, formData.firstPaymentDate, formData.totalPayments, formData.dayOfMonth]);

  // Calculate total payments for periodic payments (based on first payment date, expiration date, and payment day)
  useEffect(() => {
    if (formData.paymentType === 'periodic' && formData.firstPaymentDate && formData.expirationDate) {
      const calculateTotalPayments = () => {
        const firstDate = new Date(formData.firstPaymentDate);
        const expirationDate = new Date(formData.expirationDate);
        
        if (expirationDate <= firstDate) {
          return '1'; // At least one payment
        }
        
        // Calculate months between dates
        let months = (expirationDate.getFullYear() - firstDate.getFullYear()) * 12;
        months += expirationDate.getMonth() - firstDate.getMonth();
        
        // Add 1 because we include both start and end months
        return Math.max(1, months + 1).toString();
      };
      
      const calculatedPayments = calculateTotalPayments();
      setFormData(prev => ({ ...prev, totalPayments: calculatedPayments }));
    }
  }, [formData.paymentType, formData.firstPaymentDate, formData.expirationDate, formData.dayOfMonth]);

  // Calculate amount per payment for installment payments (based on total amount and number of payments)
  useEffect(() => {
    if (formData.paymentType === 'installment' && formData.totalAmount && formData.totalPayments && parseInt(formData.totalPayments) > 0) {
      const totalAmount = parseFloat(formData.totalAmount);
      const totalPayments = parseInt(formData.totalPayments);
      const amountPerPayment = (totalAmount / totalPayments).toFixed(2);
      
      setFormData(prev => ({ ...prev, amount: amountPerPayment }));
    }
  }, [formData.paymentType, formData.totalAmount, formData.totalPayments]);

  // Load payments on component mount
  useEffect(() => {
    loadActivePeriodicPayments().finally(() => setInitialLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load payments based on filter toggle
  useEffect(() => {
    if (isFirstMount) {
      setIsFirstMount(false);
      return;
    }
    
    if (activeFilter === 'active') {
      loadActivePeriodicPayments();
    } else {
      loadAllPeriodicPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  // Filter payments by search query and active status
  const filteredPayments = periodicPayments.filter(payment => {
    if (activeFilter === 'active' && !payment.isActive) return false;
    if (activeFilter === 'inactive' && payment.isActive) return false;
    
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const description = payment.description?.toLowerCase() || '';
    const category = payment.category?.toLowerCase() || '';
    return description.includes(query) || category.includes(query);
  });

  // Helper function to format day with proper ordinals based on language
  const formatDayWithOrdinal = (day: number) => {
    if (language === 'he') {
      return `${day}`; // Hebrew doesn't use ordinals like English
    }
    
    // English ordinals
    const suffix = day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';
    return `${day}${suffix}`;
  };

  // Helper function to format date properly based on language
  const formatExpirationDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = language === 'he' ? 'he-IL' : 'en-US';
    return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  };

  // Helper function to get category color for a payment
  const getCategoryColor = (payment: any) => {
    // Use categoryColor from server response first, then fallback to lookup or default
    return payment.categoryColor || 
           categories.find(cat => cat.name === payment.category)?.color || 
           payment.color || 
           '#6B7280'; // Default gray
  };

  const isIncomePayment = (payment: any) => {
    // Check if the payment's category is of type 'income'
    const category = categories.find(cat => cat.name === payment.category);
    return category?.type === 'income';
  };

  const getCategoryTextColor = (payment: any) => {
    const baseColor = getCategoryColor(payment);
    // Make the text color darker for better readability
    // Convert hex to RGB and darken it
    const hex = baseColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Darken the color by reducing each RGB component
    const darkenFactor = 0.7;
    const darkR = Math.floor(r * darkenFactor);
    const darkG = Math.floor(g * darkenFactor);
    const darkB = Math.floor(b * darkenFactor);
    
    return `rgb(${darkR}, ${darkG}, ${darkB})`;
  };

  // Skeleton component for loading state
  const PaymentCardSkeleton = () => (
    <Card className="p-4 space-y-4">
      {/* Header with badge */}
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Skeleton className="w-3 h-3 rounded-full" />
          <Skeleton className="h-5 w-20" />
        </div>
        <div className="flex gap-1">
          <Skeleton className="h-7 w-7" />
          <Skeleton className="h-7 w-7" />
        </div>
      </div>
      
      {/* Title and category */}
      <div className="space-y-2">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-16" />
      </div>
      
      {/* Amount and status */}
      <div className={`flex items-end justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="space-y-1">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton className="h-5 w-12" />
      </div>
      
      {/* Progress section */}
      <div className="space-y-2">
        <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-8" />
        </div>
        <Skeleton className="w-full h-1.5 rounded-full" />
        <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Skeleton className="h-3 w-12" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    </Card>
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation based on payment type
    const baseFieldsValid = formData.category && formData.description;
    let typeSpecificFieldsValid = false;
    
    if (formData.paymentType === 'periodic') {
      // For periodic payments, need amount, start date, and a repeat schedule
      typeSpecificFieldsValid = formData.amount && formData.firstPaymentDate && formData.repeatSchedule.frequency !== 'never';
    } else if (formData.paymentType === 'installment') {
      // For installment, need total amount and number of payments
      typeSpecificFieldsValid = formData.totalAmount && formData.totalPayments && parseInt(formData.totalPayments) > 0;
    }
    
    if (!baseFieldsValid || !typeSpecificFieldsValid) {
      toast({
        title: t('common.error', 'Error'),
        description: t('messages.fillAllFields', 'Please fill in all required fields'),
        variant: "destructive",
      });
      return;
    }

    // Validate that amounts are positive
    if (formData.paymentType === 'periodic') {
      const amount = parseFloat(formData.amount);
      if (amount <= 0) {
        toast({
          title: t('common.error', 'Error'),
          description: t('periodicPayments.amountMustBePositive', 'Amount must be a positive number'),
          variant: "destructive",
        });
        return;
      }
    } else if (formData.paymentType === 'installment') {
      const totalAmount = parseFloat(formData.totalAmount);
      if (totalAmount <= 0) {
        toast({
          title: t('common.error', 'Error'),
          description: t('periodicPayments.amountMustBePositive', 'Amount must be a positive number'),
          variant: "destructive",
        });
        return;
      }
    }

    // Note: remainingPayments is NOT sent - it's calculated and managed by the server
    // The server will automatically initialize it for new payments and preserve progress for updates
    
    let paymentData: any;
    
    if (formData.paymentType === 'periodic') {
      // For periodic payments, use the repeat schedule selector
      const repeatApiFields = repeatScheduleToApiFields(formData.repeatSchedule);
      
      paymentData = {
        paymentType: formData.paymentType,
        amount: -Math.abs(parseFloat(formData.amount)), // Always negative for expenses
        category: formData.category,
        description: formData.description,
        color: categories.find(cat => cat.name === formData.category)?.color || formData.color,
        dayOfMonth: repeatApiFields.dayOfMonth || formData.dayOfMonth,
        firstPaymentDate: formData.firstPaymentDate,
        totalPayments: repeatApiFields.repeatCount || parseInt(formData.totalPayments) || 999,
        expirationDate: repeatApiFields.repeatUntil || formData.expirationDate,
        isActive: formData.isActive,
        // New repeat schedule fields
        repeatFrequency: repeatApiFields.repeatFrequency,
        repeatInterval: repeatApiFields.repeatInterval,
        repeatOnDay: repeatApiFields.repeatOnDay,
        repeatOnDate: repeatApiFields.repeatOnDate,
        repeatOnMonth: repeatApiFields.repeatOnMonth,
        repeatUntil: repeatApiFields.repeatUntil,
        repeatCount: repeatApiFields.repeatCount,
      };
    } else {
      // For installments, use the selected start date directly (same logic as periodic)
      const totalPayments = parseInt(formData.totalPayments);
      const startDate = new Date(formData.firstPaymentDate);
      const dayOfMonth = startDate.getDate();
      
      // Calculate expiration date based on number of payments
      const expirationDate = new Date(startDate.getFullYear(), startDate.getMonth() + totalPayments - 1, dayOfMonth);
      
      paymentData = {
        paymentType: formData.paymentType,
        amount: -Math.abs(parseFloat(formData.amount)), // Always negative for expenses
        totalAmount: parseFloat(formData.totalAmount),
        category: formData.category,
        description: formData.description,
        color: categories.find(cat => cat.name === formData.category)?.color || formData.color,
        dayOfMonth: dayOfMonth, // Use the day from the selected start date
        firstPaymentDate: formData.firstPaymentDate,
        totalPayments: totalPayments,
        expirationDate: expirationDate.toISOString().split('T')[0],
        isActive: formData.isActive,
        // Installments are always monthly
        repeatFrequency: 'monthly',
        repeatInterval: 1,
        repeatOnDate: dayOfMonth,
        repeatCount: totalPayments,
      };
    }

    setIsSubmitting(true);
    try {
      if (editingPayment) {
        await updatePeriodicPayment(editingPayment.id, paymentData);
      } else {
        await addPeriodicPayment(paymentData);
      }

      resetForm();
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to save periodic payment:', error);
      // Error toast is already handled in the context functions
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (payment: any) => {
    setEditingPayment(payment);
    
    // Load repeat schedule from payment (with backward compatibility)
    const repeatSchedule = createRepeatScheduleFromPayment(payment);
    
    setFormData({
      paymentType: payment.paymentType || 'periodic', // Default to periodic for backward compatibility
      amount: Math.abs(payment.amount).toString(),
      totalAmount: payment.totalAmount ? payment.totalAmount.toString() : '',
      category: payment.category,
      description: payment.description,
      color: payment.color,
      dayOfMonth: payment.dayOfMonth,
      firstPaymentDate: payment.firstPaymentDate || new Date().toISOString().split('T')[0], // Default to today if not set
      totalPayments: payment.totalPayments.toString(),
      expirationDate: payment.expirationDate || '',
      isActive: payment.isActive,
      repeatSchedule,
    });
    setIsOpen(true);
  };

  const handleDeactivate = async (id: string) => {
    try {
      await updatePeriodicPayment(id, { isActive: false });
    } catch (error) {
      console.error('Failed to deactivate periodic payment:', error);
      // Error toast is already handled in the context function
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await updatePeriodicPayment(id, { isActive: true });
    } catch (error) {
      console.error('Failed to activate periodic payment:', error);
      // Error toast is already handled in the context function
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    setSyncPopoverOpen(false);
    try {
      const result = await recurringPaymentApi.sync(syncMonth, syncYear);
      const monthName = new Date(syncYear, syncMonth - 1).toLocaleDateString(
        language === 'he' ? 'he-IL' : 'en-US',
        { month: 'long', year: 'numeric' }
      );

      if (result.created > 0) {
        sonnerToast.success(
          `${t('periodicPayments.syncComplete', 'Sync complete')} — ${monthName}`,
          {
            description: `${result.created} ${t('periodicPayments.created', 'created')}, ${result.skippedDuplicate} ${t('periodicPayments.skippedDuplicate', 'already existed')}, ${result.skippedNotDue} ${t('periodicPayments.skippedNotDue', 'not due')}`,
            duration: 5000,
          }
        );
      } else {
        sonnerToast.info(
          `${t('periodicPayments.syncComplete', 'Sync complete')} — ${monthName}`,
          {
            description: `${t('periodicPayments.noNewPayments', 'No new transactions to create.')} ${result.skippedDuplicate} ${t('periodicPayments.skippedDuplicate', 'already existed')}.`,
            duration: 4000,
          }
        );
      }

      if (result.errors > 0) {
        sonnerToast.warning(
          `${result.errors} ${t('periodicPayments.syncErrors', 'errors during sync')}`,
          { duration: 4000 }
        );
      }

      // Reload payments to reflect new transactions
      if (activeFilter === 'active') {
        loadActivePeriodicPayments();
      } else {
        loadAllPeriodicPayments();
      }
    } catch (error) {
      sonnerToast.error(t('periodicPayments.syncFailed', 'Sync failed'), {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      paymentType: 'periodic',
      amount: '',
      totalAmount: '',
      category: '',
      description: '',
      color: colorOptions[0],
      dayOfMonth: 1,
      firstPaymentDate: new Date().toISOString().split('T')[0], // Default to today
      totalPayments: '',
      expirationDate: '',
      isActive: true,
      repeatSchedule: defaultRepeatSchedule,
    });
    setEditingPayment(null);
  };

  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const currentValue = parseFloat(formData.amount) || 0;
    const step = 0.01;
    
    // Block down arrow if it would make the value negative or zero
    if (e.key === 'ArrowDown' && currentValue - step <= 0) {
      e.preventDefault();
    }
  };

  const handleTotalAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const currentValue = parseFloat(formData.totalAmount) || 0;
    const step = 0.01;
    
    // Block down arrow if it would make the value negative or zero
    if (e.key === 'ArrowDown' && currentValue - step <= 0) {
      e.preventDefault();
    }
  };

  // Selecting a category also sets the payment color from that category,
  // so the color is derived from the category instead of picked manually.
  const handleCategorySelect = (value: string) => {
    const matched = categories.find((cat) => cat.name === value);
    setFormData(prev => ({
      ...prev,
      category: value,
      color: matched?.color || prev.color,
    }));
  };

  // Add a low-opacity tint of a hex color for the selected chip background.
  const withAlpha = (hex: string) =>
    /^#[0-9a-fA-F]{6}$/.test(hex) ? `${hex}22` : 'transparent';

  // Category selector rendered as colored chips (colored by the category color).
  const renderCategoryChips = () => (
    <div className={`flex flex-wrap gap-2 max-h-40 overflow-y-auto ${isRTL ? 'flex-row-reverse' : ''}`}>
      {categories.map((category) => {
        const selected = formData.category === category.name;
        return (
          <button
            key={category.id}
            type="button"
            onClick={() => handleCategorySelect(category.name)}
            aria-pressed={selected}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
              selected
                ? 'border-2 shadow-sm'
                : 'border border-border text-foreground hover:border-foreground/40'
            }`}
            style={
              selected
                ? { borderColor: category.color, backgroundColor: withAlpha(category.color) }
                : undefined
            }
          >
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: category.color }} />
            {category.name}
          </button>
        );
      })}
    </div>
  );

  // Calculate statistics - simplified for clarity
  const statistics = {
    // Total active payments count
    totalActive: filteredPayments.filter(p => p.isActive).length,
    
    // Expense payments - broken down by type
    expenseCount: filteredPayments.filter(p => p.isActive && !isIncomePayment(p)).length,
    expenseAmount: filteredPayments
      .filter(p => p.isActive && !isIncomePayment(p))
      .reduce((sum, p) => sum + Math.abs(p.amount), 0),
    expensePeriodicCount: filteredPayments.filter(p => p.isActive && !isIncomePayment(p) && p.paymentType === 'periodic').length,
    expensePeriodicAmount: filteredPayments
      .filter(p => p.isActive && !isIncomePayment(p) && p.paymentType === 'periodic')
      .reduce((sum, p) => sum + Math.abs(p.amount), 0),
    expenseInstallmentCount: filteredPayments.filter(p => p.isActive && !isIncomePayment(p) && p.paymentType === 'installment').length,
    expenseInstallmentAmount: filteredPayments
      .filter(p => p.isActive && !isIncomePayment(p) && p.paymentType === 'installment')
      .reduce((sum, p) => sum + Math.abs(p.amount), 0),
    
    // Income payments - broken down by type
    incomeCount: filteredPayments.filter(p => p.isActive && isIncomePayment(p)).length,
    incomeAmount: filteredPayments
      .filter(p => p.isActive && isIncomePayment(p))
      .reduce((sum, p) => sum + Math.abs(p.amount), 0),
    incomePeriodicCount: filteredPayments.filter(p => p.isActive && isIncomePayment(p) && p.paymentType === 'periodic').length,
    incomePeriodicAmount: filteredPayments
      .filter(p => p.isActive && isIncomePayment(p) && p.paymentType === 'periodic')
      .reduce((sum, p) => sum + Math.abs(p.amount), 0),
    incomeInstallmentCount: filteredPayments.filter(p => p.isActive && isIncomePayment(p) && p.paymentType === 'installment').length,
    incomeInstallmentAmount: filteredPayments
      .filter(p => p.isActive && isIncomePayment(p) && p.paymentType === 'installment')
      .reduce((sum, p) => sum + Math.abs(p.amount), 0),
    
    // Balance this month (income - expenses)
    balanceThisMonth: filteredPayments
      .filter(p => p.isActive)
      .reduce((sum, p) => {
        const amount = Math.abs(p.amount);
        return sum + (isIncomePayment(p) ? amount : -amount);
      }, 0)
  };

  return (
    <div className="page-shell pb-4">
      {/* Header */}
      <div className={`page-heading flex-col gap-3 md:flex-row md:items-center md:justify-between ${isRTL ? 'md:flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <h1 className={`text-xl md:text-2xl font-bold flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Calendar className="h-5 w-5 md:h-6 md:w-6 text-primary" />
            {t('periodicPayments.title')}
          </h1>
          <p className={`text-xs md:text-sm text-muted-foreground mt-1 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('periodicPayments.subtitle')}
          </p>
        </div>
        
        <div className={`flex gap-2 w-full md:w-auto ${isRTL ? 'flex-row-reverse' : ''}`}>
          {/* Sync Button */}
          <Popover open={syncPopoverOpen} onOpenChange={setSyncPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2 flex-1 md:flex-none" disabled={isSyncing}>
                {isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {t('periodicPayments.sync', 'Sync')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64" align="end">
              <div className="space-y-3">
                <p className={`text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('periodicPayments.syncMonth', 'Select month to sync')}
                </p>
                <div className="flex gap-2">
                  <Select value={syncMonth.toString()} onValueChange={(v) => setSyncMonth(parseInt(v))}>
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {new Date(2024, i).toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={syncYear.toString()} onValueChange={(v) => setSyncYear(parseInt(v))}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 5 }, (_, i) => {
                        const y = new Date().getFullYear() - 1 + i;
                        return <SelectItem key={y} value={y.toString()}>{y}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleSync} className="w-full gap-2" disabled={isSyncing}>
                  {isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  {t('periodicPayments.runSync', 'Run Sync')}
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          {/* Add Payment Button */}
          <Dialog open={isOpen} onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="gap-2 flex-1 md:flex-none">
                <Plus className="h-4 w-4" />
                {t('periodicPayments.addPayment')}
              </Button>
            </DialogTrigger>
          
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" dir={isRTL ? 'rtl' : 'ltr'}>
            <DialogHeader dir={isRTL ? 'rtl' : 'ltr'} className={isRTL ? 'text-right' : 'text-left'}>
              <DialogTitle className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                {editingPayment ? t('periodicPayments.editPayment') : t('periodicPayments.addPayment')}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
              <div className={`overflow-y-auto space-y-4 max-h-[calc(90vh-180px)] ${isRTL ? 'pl-2' : 'pr-2'}`}>
                
                {/* Payment Type Toggle - Prominent at top */}
                <div className="flex justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
                  <div className="bg-muted p-1 rounded-lg w-full max-w-sm flex">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, paymentType: 'periodic' }))}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md transition-all ${
                        formData.paymentType === 'periodic' 
                          ? 'bg-background shadow-sm font-medium' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Repeat className="h-4 w-4 shrink-0" />
                      <span>{t('periodicPayments.periodic')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, paymentType: 'installment' }))}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md transition-all ${
                        formData.paymentType === 'installment' 
                          ? 'bg-background shadow-sm font-medium' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <CreditCard className="h-4 w-4 shrink-0" />
                      <span>{t('periodicPayments.installment')}</span>
                    </button>
                  </div>
                </div>

                {/* ===== PERIODIC PAYMENT FORM ===== */}
                {formData.paymentType === 'periodic' ? (
                  <div className="space-y-4">
                    {/* Description + Monthly amount on one line */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" dir={isRTL ? 'rtl' : 'ltr'}>
                      <div className="space-y-1.5">
                        <Label htmlFor="description" className={`block text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('periodicPayments.description')}
                        </Label>
                        <Input
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder={t('periodicPayments.descriptionPlaceholder', 'e.g., Rent')}
                          className="h-10"
                          dir={isRTL ? 'rtl' : 'ltr'}
                          required
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="amount" className={`block text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('periodicPayments.monthlyAmount')} ({currency.symbol})
                        </Label>
                        <div className="relative">
                          <span className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-mono ${isRTL ? 'right-3' : 'left-3'}`}>{currency.symbol}</span>
                          <Input
                            id="amount"
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={formData.amount}
                            onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                            onKeyDown={handleAmountKeyDown}
                            placeholder="0.00"
                            className={`h-10 text-base font-semibold ${isRTL ? 'text-right pr-8' : 'text-left pl-8'}`}
                            dir="ltr"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Start Date + Repeat Schedule on one line */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start" dir={isRTL ? 'rtl' : 'ltr'}>
                      <div className="space-y-1.5">
                        <Label htmlFor="firstPaymentDate" className={`block text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('periodicPayments.repeat.startDate', 'Start Date')}
                        </Label>
                        <Input
                          id="firstPaymentDate"
                          type="date"
                          value={formData.firstPaymentDate}
                          onChange={(e) => setFormData(prev => ({ ...prev, firstPaymentDate: e.target.value }))}
                          className="h-10"
                          required
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className={`block text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('periodicPayments.repeat.repeatSchedule', 'Repeat Schedule')}
                        </Label>
                        <RepeatScheduleSelector
                          value={formData.repeatSchedule}
                          onChange={(schedule) => {
                            setFormData(prev => ({ 
                              ...prev, 
                              repeatSchedule: schedule,
                              dayOfMonth: schedule.onDate || prev.dayOfMonth,
                              expirationDate: schedule.endType === 'on' && schedule.endDate ? schedule.endDate : prev.expirationDate,
                              totalPayments: schedule.endType === 'after' && schedule.endCount ? schedule.endCount.toString() : prev.totalPayments,
                            }));
                          }}
                          startDate={new Date(formData.firstPaymentDate || new Date())}
                        />
                      </div>
                    </div>

                    {/* Compact schedule subtitle under the date/repeat row */}
                    {formData.firstPaymentDate && formData.repeatSchedule.frequency !== 'never' && (
                      <p className={`-mt-1 text-xs text-muted-foreground ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                        📅 {t('periodicPayments.repeat.startDate', 'Starts')}: {new Date(formData.firstPaymentDate).toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US')}
                        {formData.repeatSchedule.endType === 'on' && formData.repeatSchedule.endDate && (
                          <> · 🏁 {t('periodicPayments.repeat.until', 'Until')}: {new Date(formData.repeatSchedule.endDate).toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US')}</>
                        )}
                        {formData.repeatSchedule.endType === 'after' && formData.repeatSchedule.endCount && (
                          <> · 🔢 {formData.repeatSchedule.endCount} {t('periodicPayments.repeat.times', 'times')}</>
                        )}
                      </p>
                    )}

                    {/* Category - colored chips */}
                    <div className="space-y-1.5" dir={isRTL ? 'rtl' : 'ltr'}>
                      <Label className={`block text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('periodicPayments.category')}
                      </Label>
                      {renderCategoryChips()}
                    </div>
                  </div>
                ) : (
                  /* ===== INSTALLMENT PAYMENT FORM ===== */
                  <div className="space-y-4">
                    {/* Description + Total amount on one line */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" dir={isRTL ? 'rtl' : 'ltr'}>
                      <div className="space-y-1.5">
                        <Label htmlFor="description" className={`block text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('periodicPayments.description')}
                        </Label>
                        <Input
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder={t('periodicPayments.installmentPlaceholder', 'e.g., Car loan')}
                          className="h-10"
                          dir={isRTL ? 'rtl' : 'ltr'}
                          required
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="totalAmount" className={`block text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('periodicPayments.totalAmount')} ({currency.symbol})
                        </Label>
                        <div className="relative">
                          <span className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-mono ${isRTL ? 'right-3' : 'left-3'}`}>{currency.symbol}</span>
                          <Input
                            id="totalAmount"
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={formData.totalAmount}
                            onChange={(e) => setFormData(prev => ({ ...prev, totalAmount: e.target.value }))}
                            onKeyDown={handleTotalAmountKeyDown}
                            placeholder="0.00"
                            className={`h-10 text-base font-semibold ${isRTL ? 'text-right pr-8' : 'text-left pl-8'}`}
                            dir="ltr"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Number of Payments */}
                    <div className="space-y-1.5" dir={isRTL ? 'rtl' : 'ltr'}>
                      <Label htmlFor="totalPayments" className={`block text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('periodicPayments.numberOfPayments', 'Number of Payments')}
                      </Label>
                      <Input
                        id="totalPayments"
                        type="number"
                        min="1"
                        value={formData.totalPayments}
                        onChange={(e) => setFormData(prev => ({ ...prev, totalPayments: e.target.value }))}
                        placeholder="12"
                        className="h-10"
                        dir="ltr"
                        required
                      />
                    </div>

                    {/* Per Payment Display - Calculated value shown prominently */}
                    {formData.amount && (
                      <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800" dir={isRTL ? 'rtl' : 'ltr'}>
                        <span className="text-sm text-green-700 dark:text-green-300">
                          {t('periodicPayments.perPayment')}
                        </span>
                        <span className="text-lg font-bold text-green-700 dark:text-green-300" dir="ltr">
                          {currency.symbol}{formData.amount}
                        </span>
                      </div>
                    )}

                    {/* Start Date */}
                    <div className="space-y-1.5" dir={isRTL ? 'rtl' : 'ltr'}>
                      <Label htmlFor="installmentStartDate" className={`block text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('periodicPayments.repeat.startDate', 'Start Date')}
                      </Label>
                      <Input
                        id="installmentStartDate"
                        type="date"
                        value={formData.firstPaymentDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, firstPaymentDate: e.target.value }))}
                        className="h-10"
                        required
                      />
                    </div>

                    {/* Category - colored chips */}
                    <div className="space-y-1.5" dir={isRTL ? 'rtl' : 'ltr'}>
                      <Label className={`block text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('periodicPayments.category')}
                      </Label>
                      {renderCategoryChips()}
                    </div>

                    {/* Installment Summary */}
                    {formData.totalPayments && formData.amount && (
                      <div className={`p-3 bg-muted/50 rounded-lg ${isRTL ? 'border-r-4 border-r-primary' : 'border-l-4 border-l-primary'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                        <p className="text-sm text-foreground">
                          📅 {formData.totalPayments} {t('periodicPayments.payments', 'payments')}
                          <span className="mx-1">•</span>
                          <span dir="ltr">{currency.symbol}{formData.amount}</span> {t('periodicPayments.perPayment', 'each')}
                          <span className="block mt-1">
                            🗓️ {t('periodicPayments.startsOn', 'Starts on')} {new Date(formData.firstPaymentDate).toLocaleDateString(isRTL ? 'he-IL' : 'en-US')}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
              </div>
              
              {/* Footer with action buttons */}
              <div className={`flex gap-2 pt-4 pb-2 border-t mt-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)} className="flex-1" disabled={isSubmitting}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="submit" className="flex-1" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.saving', 'Saving...')}
                    </>
                  ) : (
                    editingPayment ? t('periodicPayments.updatePayment', 'Update Payment') : t('periodicPayments.addPayment', 'Add Payment')
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      </div>

      {/* Statistics Cards */}
      {initialLoading && (
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4`} dir={isRTL ? 'rtl' : 'ltr'}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="p-3 md:p-4">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-7 w-16" />
                  <Skeleton className="h-3 w-12" />
                </div>
                <Skeleton className="w-10 h-10 rounded-full" />
              </div>
            </Card>
          ))}
        </div>
      )}
      {!initialLoading && filteredPayments.length > 0 && (
        <TooltipProvider>
          <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 ${isRTL ? 'direction-rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Total Active Payments */}
            <Card className="p-3 md:p-4 bg-blue-500/12 border-blue-500/30">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <p className="text-xs text-muted-foreground mb-1">{t('periodicPayments.totalActive', 'Total Active')}</p>
                  <p className="text-2xl md:text-3xl font-bold text-blue-700 dark:text-blue-300">
                    {statistics.totalActive}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{t('periodicPayments.payments', 'payments')}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-blue-200 dark:bg-blue-900/50 flex items-center justify-center shrink-0">
                  <ListChecks className="h-5 w-5 text-blue-700 dark:text-blue-300" />
                </div>
              </div>
            </Card>

            {/* Expenses */}
            <Card className="p-3 md:p-4 bg-red-500/12 border-red-500/30">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <p className="text-xs text-muted-foreground mb-1">{t('periodicPayments.expenses', 'Expenses')}</p>
                  <p className="text-xl md:text-2xl font-bold text-red-700 dark:text-red-300">
                    {formatAmount(-statistics.expenseAmount)}
                  </p>
                  <div className="flex flex-col gap-0.5 mt-1">
                    {statistics.expensePeriodicCount > 0 && (
                      <p className="text-[10px] text-red-600/80 dark:text-red-400/80">
                        {statistics.expensePeriodicCount} {t('periodicPayments.periodic', 'constant')} • {formatAmount(-statistics.expensePeriodicAmount)}
                      </p>
                    )}
                    {statistics.expenseInstallmentCount > 0 && (
                      <p className="text-[10px] text-red-600/80 dark:text-red-400/80">
                        {statistics.expenseInstallmentCount} {t('periodicPayments.installment', 'installment')} • {formatAmount(-statistics.expenseInstallmentAmount)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="w-10 h-10 rounded-full bg-red-200 dark:bg-red-900/50 flex items-center justify-center shrink-0">
                  <TrendingUp className="h-5 w-5 text-red-700 dark:text-red-300 rotate-180" />
                </div>
              </div>
            </Card>

            {/* Incomes */}
            <Card className="p-3 md:p-4 bg-green-500/12 border-green-500/30">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <p className="text-xs text-muted-foreground mb-1">{t('periodicPayments.incomes', 'Incomes')}</p>
                  <p className="text-xl md:text-2xl font-bold text-green-700 dark:text-green-300">
                    {formatAmount(statistics.incomeAmount)}
                  </p>
                  <div className="flex flex-col gap-0.5 mt-1">
                    {statistics.incomePeriodicCount > 0 && (
                      <p className="text-[10px] text-green-600/80 dark:text-green-400/80">
                        {statistics.incomePeriodicCount} {t('periodicPayments.periodic', 'constant')} • {formatAmount(statistics.incomePeriodicAmount)}
                      </p>
                    )}
                    {statistics.incomeInstallmentCount > 0 && (
                      <p className="text-[10px] text-green-600/80 dark:text-green-400/80">
                        {statistics.incomeInstallmentCount} {t('periodicPayments.installment', 'installment')} • {formatAmount(statistics.incomeInstallmentAmount)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="w-10 h-10 rounded-full bg-green-200 dark:bg-green-900/50 flex items-center justify-center shrink-0">
                  <TrendingUp className="h-5 w-5 text-green-700 dark:text-green-300" />
                </div>
              </div>
            </Card>

            {/* Balance This Month */}
            <Card className="p-3 md:p-4 bg-primary/12 border-primary/30">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <p className="text-xs text-muted-foreground mb-1">{t('periodicPayments.balanceThisMonth', 'Balance This Month')}</p>
                  <p className={`text-2xl md:text-3xl font-bold ${statistics.balanceThisMonth >= 0 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                    {formatAmount(statistics.balanceThisMonth)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {statistics.balanceThisMonth >= 0 ? t('periodicPayments.surplus', 'surplus') : t('periodicPayments.deficit', 'deficit')}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-purple-200 dark:bg-purple-900/50 flex items-center justify-center shrink-0">
                  <DollarSign className="h-5 w-5 text-purple-700 dark:text-purple-300" />
                </div>
              </div>
            </Card>
          </div>
        </TooltipProvider>
      )}

      {/* Search and Filter Controls */}
      {initialLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full rounded-lg" />
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Skeleton className="h-8 w-20 rounded-full" />
            <Skeleton className="h-8 w-24 rounded-full" />
            <Skeleton className="h-8 w-16 rounded-full" />
          </div>
          <Skeleton className="h-4 w-28" />
        </div>
      ) : (
        <div className="space-y-3">
          {/* Search Bar */}
          <div className="relative">
            <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
            <Input
              type="text"
              placeholder={t('periodicPayments.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`${isRTL ? 'pr-10 pl-10 text-right' : 'pl-10 pr-10 text-left'} h-10`}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className={`absolute top-1/2 -translate-y-1/2 h-7 w-7 p-0 ${isRTL ? 'left-2' : 'right-2'}`}
                onClick={() => setSearchQuery('')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Filter Toggle Chips */}
          <div className={`flex items-center gap-2 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
            {(['active', 'inactive', 'all'] as const).map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  activeFilter === filter
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                    : 'bg-background text-muted-foreground border-border hover:bg-muted hover:text-foreground'
                }`}
              >
                {filter === 'active' && <Eye className="h-3 w-3" />}
                {filter === 'inactive' && <EyeOff className="h-3 w-3" />}
                {filter === 'all' && <ListChecks className="h-3 w-3" />}
                {filter === 'active' && t('periodicPayments.activeOnly', 'Active')}
                {filter === 'inactive' && t('periodicPayments.inactive', 'Inactive')}
                {filter === 'all' && t('periodicPayments.showAll', 'All')}
              </button>
            ))}
          </div>
          
          {/* Results Count */}
          <div className={`text-[10px] sm:text-xs text-muted-foreground px-1 ${isRTL ? 'text-right' : 'text-left'}`}>
            {filteredPayments.length} {filteredPayments.length === 1 ? t('periodicPayments.payment') : t('periodicPayments.payments')}
            {searchQuery && (
              <span className="ml-1">
                ({t('periodicPayments.searchResults')})
              </span>
            )}
          </div>
        </div>
      )}

      {/* Payments List */}
      {initialLoading ? (
        isMobile ? (
          <div className="grid grid-cols-1 gap-4" dir={isRTL ? 'rtl' : 'ltr'}>
            {Array.from({ length: 4 }).map((_, index) => (
              <PaymentCardSkeleton key={`skeleton-${index}`} />
            ))}
          </div>
        ) : (
          <Card className="overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <TableHead key={i}><Skeleton className="h-4 w-full rounded" /></TableHead>
                  ))}
                  <TableHead className="w-[80px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 6 }).map((_, row) => (
                  <TableRow key={row}>
                    <TableCell><div className="flex items-center gap-2"><Skeleton className="w-2 h-2 rounded-full" /><Skeleton className="h-4 w-28" /></div></TableCell>
                    <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell className="text-center"><Skeleton className="h-5 w-12 rounded-full mx-auto" /></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-[120px]">
                        <Skeleton className="flex-1 h-1.5 rounded-full" />
                        <Skeleton className="h-3 w-8" />
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-14" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )
      ) : filteredPayments.length === 0 ? (
        <Card className="p-8 md:p-12 text-center card-gradient">
          <Calendar className="h-8 w-8 md:h-10 md:w-10 text-muted-foreground mx-auto mb-3 md:mb-4" />
          <div className="text-sm md:text-base font-semibold mb-2">
            {activeFilter === 'all' ? t('periodicPayments.noPayments', 'No payments found') : activeFilter === 'inactive' ? t('periodicPayments.noInactive', 'No inactive payments') : t('periodicPayments.noActive', 'No active payments')}
          </div>
          <div className="text-xs md:text-sm text-muted-foreground mb-4">
            {activeFilter === 'active'
              ? t('periodicPayments.noActiveHint', 'No active payments found. Try showing all payments or add a new one.')
              : t('periodicPayments.noPaymentsHint', 'No payments have been created yet. Add your first payment to get started.')
            }
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            {activeFilter === 'active' && periodicPayments.length > 0 && (
              <Button variant="outline" onClick={() => setActiveFilter('all')} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ListChecks className="h-4 w-4" />
                {t('periodicPayments.showAll')}
              </Button>
            )}
            <Button onClick={() => setIsOpen(true)} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Plus className="h-4 w-4" />
              {t('periodicPayments.addPayment')}
            </Button>
          </div>
        </Card>
      ) : isMobile ? (
        /* Mobile: Card Grid */
        <div className="grid grid-cols-1 gap-4" dir={isRTL ? 'rtl' : 'ltr'}>
          {filteredPayments.map((payment) => (
            <Card 
              key={payment.id} 
              className="group relative overflow-hidden hover:shadow-md transition-all duration-200"
              style={{
                background: `linear-gradient(135deg, ${payment.color}08, ${payment.color}03)`,
                borderColor: `${payment.color}20`
              }}
            >
              <div className="p-3 space-y-2">
                <div className={`flex items-start justify-between gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`font-semibold text-[11px] leading-tight flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {payment.description}
                  </div>
                  <div className="flex gap-0.5">
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 hover:bg-muted/50" onClick={() => handleEdit(payment)}>
                      <Edit className="h-3 w-3" />
                    </Button>
                    {payment.isActive ? (
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive/70 hover:text-destructive hover:bg-destructive/10" onClick={() => handleDeactivate(payment.id)}>
                        <EyeOff className="h-3 w-3" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-green-600/70 hover:text-green-600 hover:bg-green-100/10" onClick={() => handleActivate(payment.id)}>
                        <Eye className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className={`flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Badge variant="secondary" className="text-[9px] font-semibold border text-black px-1 py-0 max-w-[140px] truncate" style={{ backgroundColor: getCategoryColor(payment) + 'CC', borderColor: getCategoryColor(payment) }}>
                    {payment.category}
                  </Badge>
                  <Badge variant="outline" className="text-[9px] px-1 py-0">
                    {payment.paymentType === 'installment' ? <><CreditCard className="h-1.5 w-1.5 mr-0.5" />{t('periodicPayments.installment')}</> : <><Repeat className="h-1.5 w-1.5 mr-0.5" />{t('periodicPayments.periodic')}</>}
                  </Badge>
                </div>
                
                <div className={`flex items-end justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={isRTL ? 'text-right' : 'text-left'}>
                    <div className={`text-[11px] font-bold ${isIncomePayment(payment) ? 'text-green-600' : 'text-expense'}`}>
                      {formatAmount(payment.amount)}
                    </div>
                    {payment.paymentType === 'installment' && payment.totalAmount && (
                      <div className="text-[10px] text-muted-foreground">{t('periodicPayments.total', 'Total')}: {formatAmount(payment.totalAmount)}</div>
                    )}
                  </div>
                  <Badge variant={payment.isActive ? "default" : "secondary"} className="text-[9px] px-1 py-0">
                    {payment.isActive ? t('periodicPayments.active') : t('periodicPayments.inactive')}
                  </Badge>
                </div>
                
                <div className="space-y-1.5">
                  {(() => {
                    const mCompleted = payment.completedPayments ?? ((payment.totalPayments || 0) - (payment.remainingPayments || 0));
                    const mTotal = payment.totalPayments || 0;
                    const mHasFiniteEnd = mTotal > 0 && mTotal < 999 && (payment.paymentType === 'installment' || !!payment.expirationDate);
                    const mPct = mHasFiniteEnd && mTotal > 0 ? Math.min((mCompleted / mTotal) * 100, 100) : 0;
                    return (
                      <>
                        {mHasFiniteEnd && (
                          <div className="w-full bg-muted/50 rounded-full h-1.5">
                            <div className="bg-gradient-to-r from-primary to-primary/80 h-1.5 rounded-full transition-all duration-300" style={{ width: `${Math.max(mPct, mCompleted > 0 ? 2 : 0)}%` }} />
                          </div>
                        )}
                        <div className={`flex flex-row items-center justify-between text-[11px] text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span>{mHasFiniteEnd ? `${Math.min(mCompleted, mTotal)}/${mTotal}` : `${mCompleted} ${t('periodicPayments.payments', 'payments')}`}</span>
                          {payment.expirationDate && mHasFiniteEnd && <span>{t('periodicPayments.lastPaymentOn')}{formatExpirationDate(payment.expirationDate)}</span>}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        /* Desktop: Table View */
        <Card className="overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className={`w-[200px] ${isRTL ? 'text-right' : 'text-left'}`}>{t('periodicPayments.description', 'Description')}</TableHead>
                <TableHead className={isRTL ? 'text-right' : 'text-left'}>{t('periodicPayments.category', 'Category')}</TableHead>
                <TableHead className={isRTL ? 'text-right' : 'text-left'}>{t('periodicPayments.type', 'Type')}</TableHead>
                <TableHead className={isRTL ? 'text-left' : 'text-right'}>{t('periodicPayments.monthlyAmount', 'Amount')}</TableHead>
                <TableHead className="text-center">{t('periodicPayments.status', 'Status')}</TableHead>
                <TableHead className="text-center">{t('periodicPayments.progress', 'Progress')}</TableHead>
                <TableHead className={isRTL ? 'text-right' : 'text-left'}>{t('periodicPayments.endDate', 'End Date')}</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.map((payment) => {
                const completed = payment.completedPayments ?? ((payment.totalPayments || 0) - (payment.remainingPayments || 0));
                const total = payment.totalPayments || 0;
                const hasFiniteEnd = total > 0 && total < 999 && (payment.paymentType === 'installment' || !!payment.expirationDate);
                const progressPct = hasFiniteEnd && total > 0 ? Math.min(Math.round((completed / total) * 100), 100) : 0;

                return (
                  <TableRow key={payment.id} className="group hover:bg-muted/20 transition-colors">
                    <TableCell className={`font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: payment.color }} />
                        <span className="truncate max-w-[180px]">{payment.description}</span>
                      </div>
                    </TableCell>
                    <TableCell className={isRTL ? 'text-right' : 'text-left'}>
                      <Badge variant="secondary" className="text-[10px] font-semibold border text-black px-1.5 py-0" style={{ backgroundColor: getCategoryColor(payment) + 'CC', borderColor: getCategoryColor(payment) }}>
                        {payment.category}
                      </Badge>
                    </TableCell>
                    <TableCell className={isRTL ? 'text-right' : 'text-left'}>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1">
                        {payment.paymentType === 'installment' ? <><CreditCard className="h-2.5 w-2.5" />{t('periodicPayments.installment')}</> : <><Repeat className="h-2.5 w-2.5" />{t('periodicPayments.periodic')}</>}
                      </Badge>
                    </TableCell>
                    <TableCell className={`font-semibold ${isRTL ? 'text-left' : 'text-right'} ${isIncomePayment(payment) ? 'text-green-600' : 'text-red-600'}`}>
                      {formatAmount(payment.amount)}
                      {payment.paymentType === 'installment' && payment.totalAmount && (
                        <div className="text-[10px] font-normal text-muted-foreground">{t('periodicPayments.total', 'Total')}: {formatAmount(payment.totalAmount)}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={payment.isActive ? "default" : "secondary"} className="text-[10px]">
                        {payment.isActive ? t('periodicPayments.active') : t('periodicPayments.inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {hasFiniteEnd ? (
                        <div className="flex items-center gap-2 min-w-[120px]">
                          <div className="flex-1 bg-muted/50 rounded-full h-1.5">
                            <div className="bg-gradient-to-r from-primary to-primary/80 h-1.5 rounded-full transition-all" style={{ width: `${Math.max(progressPct, completed > 0 ? 2 : 0)}%` }} />
                          </div>
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap">{Math.min(completed, total)}/{total}</span>
                        </div>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">{completed} {t('periodicPayments.payments', 'payments')}</span>
                      )}
                    </TableCell>
                    <TableCell className={`text-xs text-muted-foreground ${isRTL ? 'text-right' : 'text-left'}`}>
                      {payment.expirationDate ? formatExpirationDate(payment.expirationDate) : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(payment)}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        {payment.isActive ? (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive/70 hover:text-destructive" onClick={() => handleDeactivate(payment.id)}>
                            <EyeOff className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600/70 hover:text-green-600" onClick={() => handleActivate(payment.id)}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}